// Copilot structured-answer helpers — the single-source-of-truth core.
//
// CURRENT protocol (name-based, live): the model returns ONE JSON object
// {answer, discussed:[{id,why}], citations}. `answer` is markdown prose that
// names methods by their exact human-readable NAME (bolded), never an id or
// marker. `discussed` is resolved against the candidate set (resolveMethods /
// resolveDiscussed) and drives BOTH the highlight set and the comparison table,
// so the prose and the table can never name different methods (the old bug).
//
// methodId / resolveDiscussed / markerIdsInProse below implement an EARLIER
// [m_...] id-marker protocol (the model citing methods as "[m_vgn]" inline).
// That protocol is superseded by the name-based one above, but these helpers
// are kept — they are still exercised by this module's own locked test
// contract (answer-synthesis.test.js) — so they remain exported and unused by
// the live prompt/pipeline rather than deleted.
// Pure + unit-testable; nothing throws.

const STOP = new Set(['the', 'a', 'an', 'of', 'for', 'and', 'to', 'in', 'on', 'with',
  'is', 'are', 'as', 'by', 'how', 'what', 'which', 'that', 'this', 'methods', 'method',
  'grasp', 'grasping', 'planning', 'robot', 'robotic', 'scenes', 'scene', 'using', 'use']);

function norm(s) {
  return String(s || '').replace(/[^\p{L}\p{N}]+/gu, '').toLowerCase();
}
function slug(s) {
  return String(s || '').replace(/^[^\p{L}\p{N}]+/u, '').replace(/[^a-z0-9]+/gi, '-').toLowerCase().replace(/^-|-$/g, '');
}
function tokens(s) {
  return new Set((String(s || '').toLowerCase().match(/[a-z0-9]+/g) || []).filter(t => t.length > 2 && !STOP.has(t)));
}

/** Stable candidate id for a method name, e.g. "Volumetric Grasping Network (VGN)" -> "m_volumetric-grasping-network-vgn". */
export function methodId(name) {
  return 'm_' + slug(name);
}

/** Decode a JSON string body (the chars between the quotes) that may be TRUNCATED
 * mid-string. Turns \n \t \" \\ \uXXXX etc. into their characters; a dangling backslash
 * at a truncation point is dropped. */
function decodeJsonStringBody(body) {
  return String(body)
    .replace(/\\(u[0-9a-fA-F]{4}|["\\/bfnrt])/g, (m, esc) => {
      switch (esc[0]) {
        case 'n': return '\n'; case 't': return '\t'; case 'r': return '\r';
        case 'b': return '\b'; case 'f': return '\f';
        case '"': return '"'; case '\\': return '\\'; case '/': return '/';
        case 'u': return String.fromCharCode(parseInt(esc.slice(1), 16));
        default: return m;
      }
    })
    .replace(/\\$/, ''); // drop a lone trailing backslash left by a mid-escape truncation
}

/**
 * salvageAnswer(s) -> the `answer` field's prose, recovered even when the surrounding JSON
 * was TRUNCATED mid-string (the model hit its token limit before closing the object). Reads
 * the "answer":"..." value honoring \" escapes, up to the first UNescaped closing quote OR
 * the end of the (cut-off) input, then unescapes it. Returns '' when there is no answer key.
 * This is what stops a cut-off response from ever surfacing as a raw {"answer":"...\n\n... .
 */
export function salvageAnswer(s) {
  const str = String(s || '');
  const key = str.search(/"answer"\s*:\s*"/);
  if (key === -1) return '';
  const colon = str.indexOf(':', key);
  const open = str.indexOf('"', colon + 1); // opening quote of the value
  if (open === -1) return '';
  let body = '';
  for (let i = open + 1; i < str.length; i++) {
    const ch = str[i];
    if (ch === '\\') { body += ch + (str[i + 1] || ''); i++; continue; } // keep the escape pair
    if (ch === '"') break; // unescaped closing quote -> end of the value
    body += ch;
  }
  return decodeJsonStringBody(body).trim();
}

/** Normalize a fully-parsed structured object into {answer, discussed, citations}. */
function normalizeStructured(obj) {
  const rawDiscussed = Array.isArray(obj.discussed) ? obj.discussed
    : Array.isArray(obj.methods) ? obj.methods : [];
  const discussed = rawDiscussed.map(d => {
    if (typeof d === 'string') return { id: d.trim(), why: '' };
    if (d && typeof d === 'object') return { id: String(d.id || d.method || '').trim(), why: String(d.why || '').trim() };
    return null;
  }).filter(d => d && d.id);
  return {
    answer: obj.answer.trim(),
    discussed,
    citations: Array.isArray(obj.citations)
      ? obj.citations.filter(c => c && typeof c === 'object').map(c => ({
          marker: String(c.marker || c.paper_id || '').trim(),
          paper_id: String(c.paper_id || c.marker || '').trim(),
          paper_title: String(c.paper_title || '').trim(),
        }))
      : [],
  };
}

/**
 * parseStructuredAnswer(raw) -> {answer, discussed:[{id,why}], citations} | null.
 * Robust to code fences, leading/trailing prose, and minor noise: extracts the outermost
 * {...} and JSON.parses it. Accepts `discussed` ([{id,why}] or [id]) and tolerates a legacy
 * `methods` (array of strings) by mapping it onto discussed. If the JSON is TRUNCATED or
 * malformed (e.g. the model ran out of tokens mid-answer), it FALLS BACK to salvaging just
 * the answer prose so a cut-off response renders as clean text rather than raw JSON. Returns
 * null only when there is no recoverable `answer` at all.
 */
export function parseStructuredAnswer(raw) {
  if (!raw || typeof raw !== 'string') return null;
  let s = raw.trim();
  const fence = s.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence) s = fence[1].trim();
  const first = s.indexOf('{');
  if (first === -1) return null;
  const last = s.lastIndexOf('}');
  // Fast path: a well-formed object (has a plausible closing brace) parses strictly, which
  // keeps discussed + citations intact.
  if (last > first) {
    try {
      const obj = JSON.parse(s.slice(first, last + 1));
      if (obj && typeof obj === 'object' && typeof obj.answer === 'string' && obj.answer.trim()) {
        return normalizeStructured(obj);
      }
    } catch { /* truncated/malformed -> salvage below */ }
  }
  // Salvage path: recover just the answer prose. A truncated discussed/citations can't be
  // trusted, so leave them empty (the pipeline falls back to the top-ranked candidates for
  // the comparison table). This guarantees the raw {"answer":"... envelope never leaks.
  const salvaged = salvageAnswer(s.slice(first));
  return salvaged ? { answer: salvaged, discussed: [], citations: [] } : null;
}

/** Ids of the form m_<slug> actually written as [m_...] markers in the prose. */
export function markerIdsInProse(answer) {
  const out = new Set();
  const re = /\[(m_[a-z0-9-]+)\]/gi;
  let m;
  while ((m = re.exec(String(answer || ''))) !== null) out.add(m[1].toLowerCase());
  return out;
}

/**
 * resolveDiscussed(discussed, candidatesById) -> ordered list of {id, name} for the
 * selected methods, by EXACT id lookup against the candidate set we supplied (ids
 * never cross a string-matching boundary). Unknown ids are dropped. Deduped.
 */
export function resolveDiscussed(discussed, candidatesById) {
  const out = [];
  const seen = new Set();
  for (const d of (discussed || [])) {
    // Models often echo the id WITH its [brackets] ("[m_vgn]") — strip them so the
    // canonical candidate id ("m_vgn") matches; push the canonical key, not the raw.
    const id = String(d.id || '').replace(/[[\]\s]/g, '').toLowerCase();
    const cand = candidatesById.get ? candidatesById.get(id) : candidatesById[id];
    if (cand && !seen.has(id)) { seen.add(id); out.push({ id, name: cand.name, why: d.why || '' }); }
  }
  return out;
}

/**
 * resolveMethods(llmMethods, allMethods) -> dataset method names (in the model's
 * order, deduped) that resolve. Match: exact-normalized, then no-separator, then
 * strong token-subset (handles "(VGN)" suffixes, emoji prefixes, minor renames).
 */
export function resolveMethods(llmMethods, allMethods) {
  const names = (allMethods || []).map(m => (m && m.name) || m).filter(Boolean);
  const byNorm = new Map();
  const tokIndex = names.map(n => ({ name: n, tok: tokens(n), norm: norm(n) }));
  names.forEach(n => { if (!byNorm.has(norm(n))) byNorm.set(norm(n), n); });

  const out = [];
  const seen = new Set();
  for (const raw of (llmMethods || [])) {
    const want = String(raw || '');
    const wn = norm(want);
    if (!wn) continue;
    let hit = byNorm.get(wn);
    if (!hit) {
      // strong token-subset: the LLM name's tokens are a subset of a method's (or vice versa)
      const wt = tokens(want);
      if (wt.size) {
        let best = null;
        for (const cand of tokIndex) {
          if (!cand.tok.size) continue;
          const inter = [...wt].filter(t => cand.tok.has(t)).length;
          const subset = inter === wt.size || inter === cand.tok.size;
          if (subset && inter >= 1) {
            const score = inter / Math.max(wt.size, cand.tok.size);
            if (!best || score > best.score) best = { name: cand.name, score };
          }
        }
        if (best && best.score >= 0.5) hit = best.name;
      }
    }
    if (hit && !seen.has(hit)) { seen.add(hit); out.push(hit); }
  }
  return out;
}

/**
 * rankCandidates(allMethods, {filterMethods, ragPapers, ragCitations, query}) ->
 * method names ordered by query relevance, so the prompt's candidate list LEADS
 * with relevant methods (not array order) and the model can actually pick them.
 */
export function rankCandidates(allMethods, { filterMethods = [], ragPapers = [], ragCitations = [], query = '' } = {}) {
  const filterSet = new Set(filterMethods || []);
  const ragSet = new Set((ragPapers || []).map(slug));
  const citeText = (ragCitations || []).map(c => `${c.paper_title || ''} ${c.paper_id || ''}`.toLowerCase());
  const qTok = tokens(query);

  return (allMethods || [])
    .map((m, idx) => {
      const name = m.name;
      const mtok = tokens(name);
      let score = 0;
      if (filterSet.has(name)) score += 4;
      if (ragSet.has(slug(name))) score += 3;
      if (citeText.some(ct => [...mtok].some(t => ct.includes(t)))) score += 2;
      if ([...mtok].some(t => qTok.has(t))) score += 1;
      return { name, score, idx };
    })
    .sort((a, b) => (b.score - a.score) || (a.idx - b.idx))
    .map(x => x.name);
}
